// Dev-only, in-browser fetch interception for the Live Updates stack toolbar.
// Unlike the Playwright e2e mock (e2e/mockApi.ts, which intercepts at the
// browser-context network layer via context.route), this runs inside the
// real dev-server page itself — the only network seam available to a plain
// `yarn start` session — so it wraps `window.fetch`, the transport
// fetchBaseQuery (src/api/index.ts) actually calls. Only the Live Updates +
// Olo webhook endpoints are matched; every other request (including every
// other page's) falls through to the real network untouched.
import type {
  LiveUpdatesConfigurationDetailWire,
  LiveUpdatesConfigurationsWire,
  LiveUpdatesPublishBodyWire,
  LiveUpdatesState,
  LiveUpdatesStatusWire,
  LiveUpdatesSummaryWire,
} from 'api/liveUpdates.types'
import type { OloWebhookCredentialResponseWire } from 'api/oloWebhookCredential.types'
import type { OrderStatusWebhookReceiptsResponseWire } from 'api/orderStatusWebhookReceipts.types'
import { locationsFixture } from '../fixtures'
import {
  DEFAULT_OLO_WEBHOOK_PRESET_KEY,
  findOloWebhookPreset,
  OloWebhookPresetKey,
} from './oloWebhookPresets'
import { STACK_PRESETS } from './stackPresets'

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

// REACT_APP_API_URL carries a trailing slash (see e2e/mockApi.ts), so a
// resolved request path can come through as "...//live_updates/summary".
const canonicalPath = (pathname: string): string =>
  pathname.replace(/\/{2,}/g, '/')

const json = (body: unknown, status = 200): Response =>
  new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  })

const OLO_WEBHOOK_URL =
  'https://dev.thanxsandbox.io/webhooks/olo/v1/dev/order_status'

// The one piece of real mutable state this mock carries: which Olo webhook
// preset is currently selected. Everything else (which stack a request's
// :id names) is fully determined by the request itself, so no other global
// "current selection" is needed.
let currentOloPresetKey: OloWebhookPresetKey = DEFAULT_OLO_WEBHOOK_PRESET_KEY

export function setCurrentOloWebhookPreset(key: OloWebhookPresetKey): void {
  currentOloPresetKey = key
}

export function getCurrentOloWebhookPreset(): OloWebhookPresetKey {
  return currentOloPresetKey
}

// Per-id, in-memory detail store seeded from the static presets — mutated in
// place by publish so a merchant's edits persist for the rest of the tab's
// session, mirroring e2e/mockApi.ts's `details` map.
const detailsById = new Map<string, LiveUpdatesConfigurationDetailWire>()

const configurationsResponse: LiveUpdatesConfigurationsWire = {
  configurations: [],
  unassigned_locations_count: 0,
}

const seedPresetStores = (): void => {
  detailsById.clear()
  STACK_PRESETS.forEach(preset => {
    detailsById.set(preset.id, clone(preset.detail))
  })
  configurationsResponse.configurations = STACK_PRESETS.map(preset =>
    clone(preset.row)
  )
}

seedPresetStores()

const summaryResponse: LiveUpdatesSummaryWire = {
  summary: {
    eligible: true,
    configurations_count: STACK_PRESETS.length,
    flybuy: true,
    warnings: [],
  },
}

const CONTENT_FIELDS: (keyof LiveUpdatesStatusWire['content'])[] = [
  'status_title',
  'push_message_text',
  'homescreen_text',
]

// No validation and no optimistic-lock rejection (unlike the Playwright
// mock) — this toolbar exists to VIEW permutations, not to exercise publish
// edge cases; the e2e suite already covers that contract thoroughly. Applies
// every submitted field verbatim and always succeeds.
const applyPublish = (
  detail: LiveUpdatesConfigurationDetailWire,
  body: LiveUpdatesPublishBodyWire
): LiveUpdatesConfigurationDetailWire => {
  body.statuses.forEach(submitted => {
    const status = detail.configuration.statuses.find(
      candidate => candidate.status === submitted.status
    )
    if (!status) return

    if (submitted.enabled !== undefined) status.enabled = submitted.enabled
    if (submitted.trigger_mode !== undefined) {
      status.trigger_mode = submitted.trigger_mode
    }
    if (submitted.timer_offset !== undefined) {
      status.timer_offset = submitted.timer_offset
    }
    CONTENT_FIELDS.forEach(field => {
      const value = submitted[field]
      if (value === undefined) return
      const spec = status.content[field]
      if (!spec) return
      spec.value = value === '' ? null : value
    })
    status.source = 'custom'
  })
  detail.configuration.current_version += 1
  const publishedAt = new Date().toISOString()
  detail.configuration.customized = true
  detail.configuration.last_published_at = publishedAt
  const row = configurationsResponse.configurations.find(
    candidate => candidate.id === detail.configuration.id
  )
  if (row) {
    row.customized = true
    row.last_published_at = publishedAt
  }
  return detail
}

const LIVE_UPDATES_SUMMARY_RE = /\/live_updates\/summary$/
const LIVE_UPDATES_DISABLE_RE = /\/live_updates\/disable$/
const LIVE_UPDATES_CONFIGURATIONS_RE = /\/live_updates\/configurations$/
const LIVE_UPDATES_LOCATIONS_RE =
  /\/live_updates\/configurations\/([^/]+)\/locations$/
const LIVE_UPDATES_DETAIL_RE = /\/live_updates\/configurations\/([^/]+)$/
const OLO_WEBHOOK_CREDENTIALS_RE = /\/olo_webhook_credentials$/
const ORDER_STATUS_WEBHOOK_RECEIPTS_RE = /\/order_status_webhook_receipts$/

let mockState: LiveUpdatesState = 'enabled'

export function resetDevStackMockState(): void {
  mockState = 'enabled'
  seedPresetStores()
}

// Returns the mocked Response for a request this toolbar owns, or null when
// the request isn't one of ours (caller falls through to the real fetch).
export async function matchDevStackMock(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): Promise<Response | null> {
  const urlString =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.toString()
      : input.url
  const url = new URL(urlString, window.location.origin)
  const path = canonicalPath(url.pathname)
  const method = (init?.method ?? 'GET').toUpperCase()

  if (method === 'GET' && LIVE_UPDATES_SUMMARY_RE.test(path)) {
    return json(summaryResponse)
  }
  if (method === 'PUT' && LIVE_UPDATES_DISABLE_RE.test(path)) {
    mockState = 'disabled'
    return new Response(null, { status: 204 })
  }
  if (method === 'GET' && LIVE_UPDATES_CONFIGURATIONS_RE.test(path)) {
    return json(configurationsResponse)
  }
  if (method === 'GET' && LIVE_UPDATES_LOCATIONS_RE.test(path)) {
    return json(locationsFixture)
  }

  const detailMatch = path.match(LIVE_UPDATES_DETAIL_RE)
  if (detailMatch) {
    const detail = detailsById.get(detailMatch[1])
    if (!detail) return json({}, 404)
    if (method === 'GET') return json(detail)
    if (method === 'PUT') {
      const bodyText = typeof init?.body === 'string' ? init.body : '{}'
      const body: LiveUpdatesPublishBodyWire = JSON.parse(bodyText || '{}')
      if (mockState === 'disabled' && !body.enable_live_updates) {
        return json({ errors: { code: 'live_updates_disabled' } }, 422)
      }
      mockState = 'enabled'
      return json(applyPublish(detail, body))
    }
  }

  if (OLO_WEBHOOK_CREDENTIALS_RE.test(path)) {
    if (method === 'GET') {
      const preset = findOloWebhookPreset(currentOloPresetKey)
      const response: OloWebhookCredentialResponseWire = {
        olo_webhook_credential: {
          webhook_url: OLO_WEBHOOK_URL,
          configured: preset.configured,
        },
      }
      return json(response)
    }
    if (method === 'PUT') {
      // Simulate a fresh save immediately followed by the first event.
      currentOloPresetKey = 'received_recently'
      const response: OloWebhookCredentialResponseWire = {
        olo_webhook_credential: {
          webhook_url: OLO_WEBHOOK_URL,
          configured: true,
        },
      }
      return json(response)
    }
  }

  if (method === 'GET' && ORDER_STATUS_WEBHOOK_RECEIPTS_RE.test(path)) {
    const preset = findOloWebhookPreset(currentOloPresetKey)
    const response: OrderStatusWebhookReceiptsResponseWire =
      preset.lastReceivedAt
        ? [
            {
              location_id: 1,
              provider: 'olo',
              last_received_at: preset.lastReceivedAt,
            },
          ]
        : []
    return json(response)
  }

  return null
}

// Holds the exact pre-install `window.fetch` reference (never a .bind()
// wrapper around it) so teardown restores an identical reference rather than
// a lookalike — a second install() while already installed must not layer a
// wrapper around a wrapper.
let previousFetch: typeof window.fetch | null = null

// Idempotent: calling this while already installed is a no-op (returns the
// existing teardown). The toolbar calls this once on mount.
export function installDevStackMock(): () => void {
  if (previousFetch) {
    const restore = previousFetch
    return () => {
      window.fetch = restore
      previousFetch = null
    }
  }

  const original = window.fetch
  previousFetch = original
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const mocked = await matchDevStackMock(input, init)
    // .call(window, ...): `fetch` is spec'd to throw "Illegal invocation" when
    // called unbound (its receiver must be a Window/WorkerGlobalScope), which
    // a plain `original(input, init)` would be.
    return mocked ?? original.call(window, input, init)
  }

  return () => {
    window.fetch = original
    previousFetch = null
  }
}
